<script>
/**
 * Per-record "Qayta jo'natish" (Bitrix) for /application-show/:id and /contract_2026_2027-show/:id.
 *
 * Click → the dialog opens and immediately runs the LIVE Bitrix pre-flight
 * (GET admin/bitrix-resend/check): does a contact + deal exist for this student's phone, where does the
 * deal sit, and which events would be sent. The admin then confirms, and POST admin/bitrix-resend/send
 * enqueues them and returns the per-step report that replaces the plan.
 *
 * Pass exactly one of applicationId / contractId — the server resolves the other side. On the contract
 * page pass contractId, so the field events are built from the CONTRACT's current values (edits made
 * after the student moved to /contract_2026_2027 reach Bitrix only that way).
 */
export default {
  name: "BitrixResendButton",

  props: {
    applicationId: { type: [Number, String], default: null },
    contractId: { type: [Number, String], default: null },
    disabled: { type: Boolean, default: false },
  },

  data: () => ({
    dialog: false,
    checking: false,
    sending: false,
    checkError: false,
    check: null,
    result: null,
  }),

  computed: {
    // Which record we are asking about — contractId wins so the contract page sends contract values.
    query() {
      return this.contractId
        ? `contractId=${this.contractId}`
        : `applicationId=${this.applicationId}`;
    },

    steps() {
      const list = this.result ? this.result.steps : this.check && this.check.plan;
      return list || [];
    },

    // The send's own notice wins once it has run, so the banner describes what happened, not what was planned.
    notice() {
      if (this.result && this.result.notice) return this.result.notice;
      return this.check ? this.check.notice : null;
    },

    willSend() {
      return this.steps.filter((s) => s.send);
    },

    willSkip() {
      return this.steps.filter((s) => !s.send);
    },

    // Nothing to do → the confirm button stays disabled (referral applicants, or a record with no
    // sendable step at all), so a click can never produce an empty resend.
    canSend() {
      return !!this.check && !this.checking && this.willSend.length > 0;
    },
  },

  methods: {
    open() {
      this.dialog = true;
      this.result = null;
      this.runCheck();
    },

    close() {
      this.dialog = false;
      this.check = null;
      this.result = null;
      this.checkError = false;
    },

    async runCheck() {
      this.checking = true;
      this.checkError = false;
      this.check = null;
      try {
        const { data } = await this.$axios.get(`admin/bitrix-resend/check?${this.query}`);
        this.check = data;
      } catch (e) {
        this.checkError = true;
      } finally {
        this.checking = false;
      }
    },

    async onSend() {
      this.sending = true;
      try {
        const { data } = await this.$axios.post(`admin/bitrix-resend/send?${this.query}`);
        this.result = data;
        this.$store.dispatch("SET_SNACKBAR", {
          text: `${this.$t("Navbatga qo'yildi")}: ${data.queued}`,
          color: "success",
        });
      } catch (e) {
        this.$store.dispatch("SET_SNACKBAR", {
          text: this.$t("Qayta yuborishda xatolik"),
          color: "error",
        });
      } finally {
        this.sending = false;
      }
    },

    // Step labels come from the server in Uzbek; translate through i18n so ru/en render too.
    // The `reason` beside each step is deliberately NOT translated — it is diagnostic text that carries
    // live values ("Deal #91043 mavjud", "Deal bosqichi orqada (SHARTNOMA YUBORILDI)"), so it cannot be a
    // stable i18n key. Same convention the Bitrix monitor already uses for its server-side messages.
    stepLabel(step) {
      return step.label ? this.$t(step.label) : step.step;
    },
  },
};
</script>

<template>
  <div class="bx-resend">
    <v-btn
      class="bx-btn"
      elevation="0"
      :disabled="disabled"
      @click="open"
    >
      <img src="/icons/bitrix-icon.svg" alt="Bitrix" class="bx-btn-icon">
      {{ $t("Qayta jo'natish") }}
    </v-btn>

    <v-dialog v-model="dialog" max-width="560px" scrollable @click:outside="close">
      <v-card class="bx-card">
        <div class="bx-head">
          <img src="/icons/bitrix-icon.svg" alt="Bitrix" class="bx-head-icon">
          <div>
            <div class="bx-title">{{ $t("Bitrixga qayta jo'natish") }}</div>
            <div v-if="check && check.fullName" class="bx-subtitle">{{ check.fullName }}</div>
          </div>
        </div>

        <v-card-text class="bx-body">
          <!-- Pre-flight running -->
          <div v-if="checking" class="bx-loading">
            <v-progress-circular indeterminate size="26" width="3" color="#0c1322" />
            <span>{{ $t("Bitrixda tekshirilmoqda") }}...</span>
          </div>

          <div v-else-if="checkError" class="bx-alert bx-alert--error">
            {{ $t("Tekshirishda xatolik") }}
          </div>

          <template v-else-if="check">
            <!-- What exists in Bitrix right now -->
            <div class="bx-facts">
              <div class="bx-fact">
                <span class="bx-fact-key">{{ $t("Telefon") }}</span>
                <span class="bx-fact-val">{{ check.phone || "—" }}</span>
              </div>
              <div class="bx-fact">
                <span class="bx-fact-key">{{ $t("Kontakt") }}</span>
                <span class="bx-fact-val" :class="!!check.contactId ? 'is-ok' : 'is-miss'">
                  <v-icon size="16">{{ !!check.contactId ? "mdi-check-circle" : "mdi-alert-circle-outline" }}</v-icon>
                  {{ !!check.contactId ? "#" + check.contactId : $t("Topilmadi") }}
                </span>
              </div>
              <div class="bx-fact">
                <span class="bx-fact-key">{{ $t("Deal") }}</span>
                <span class="bx-fact-val" :class="!!check.dealId ? 'is-ok' : 'is-miss'">
                  <v-icon size="16">{{ !!check.dealId ? "mdi-check-circle" : "mdi-alert-circle-outline" }}</v-icon>
                  {{ !!check.dealId ? "#" + check.dealId : $t("Topilmadi") }}
                </span>
              </div>
              <div v-if="check.dealStageName" class="bx-fact">
                <span class="bx-fact-key">{{ $t("Bosqich") }}</span>
                <span class="bx-fact-val bx-stage">{{ check.dealStageName }}</span>
              </div>
            </div>

            <div v-if="notice" class="bx-alert" :class="{ 'bx-alert--warn': !check.dealId || check.blocked }">
              {{ notice }}
            </div>

            <!-- Plan before sending, actual outcome after -->
            <div v-if="willSend.length" class="bx-group">
              <div class="bx-group-title bx-group-title--send">
                {{ result ? $t("Navbatga qo'yildi") : $t("Jo'natiladi") }} · {{ willSend.length }}
              </div>
              <div v-for="s in willSend" :key="'s-' + s.step" class="bx-step">
                <v-icon size="16" class="bx-step-icon is-ok">mdi-arrow-up-circle</v-icon>
                <span class="bx-step-label">{{ stepLabel(s) }}</span>
                <span v-if="s.reason" class="bx-step-reason">{{ s.reason }}</span>
              </div>
            </div>

            <div v-if="willSkip.length" class="bx-group">
              <div class="bx-group-title">{{ $t("O'tkazib yuboriladi") }} · {{ willSkip.length }}</div>
              <div v-for="s in willSkip" :key="'k-' + s.step" class="bx-step bx-step--skip">
                <v-icon size="16" class="bx-step-icon">mdi-minus-circle-outline</v-icon>
                <span class="bx-step-label">{{ stepLabel(s) }}</span>
                <span v-if="s.reason" class="bx-step-reason">{{ s.reason }}</span>
              </div>
            </div>

            <div v-if="!steps.length" class="bx-alert bx-alert--warn">
              {{ $t("Hech narsa jo'natilmaydi") }}
            </div>
          </template>
        </v-card-text>

        <v-card-actions class="bx-actions">
          <v-spacer />
          <v-btn text @click="close">{{ result ? $t("Yopish") : $t("Bekor qilish") }}</v-btn>
          <v-btn
            v-if="!result"
            depressed
            color="#0c1322"
            class="white--text"
            :loading="sending"
            :disabled="!canSend || sending"
            @click="onSend"
          >
            {{ $t("Jo'natish") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.bx-resend {
  display: inline-flex;
}

/* Same white pill the applications list uses for its bulk re-send, so the two read as one action. */
.bx-btn {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  background: #fff !important;
  color: #344054 !important;
  border: 1px solid #d0d5dd !important;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600 !important;
  letter-spacing: .03em;
  text-transform: none !important;
  transition: all .2s ease;
  height: 36px !important;
}
.bx-btn:hover {
  background: #f9fafb !important;
  border-color: #c0c7d0 !important;
}
.bx-btn:active {
  transform: scale(.97);
}
.bx-btn-icon {
  width: 18px;
  height: 18px;
  margin-right: 10px;
}

.bx-card {
  border-radius: 14px;
}
.bx-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px 14px;
  border-bottom: 1px solid #eceff3;
}
.bx-head-icon {
  width: 26px;
  height: 26px;
}
.bx-title {
  font-size: 16px;
  font-weight: 700;
  color: #0c1322;
  letter-spacing: .2px;
}
.bx-subtitle {
  font-size: 13px;
  color: #667085;
  margin-top: 2px;
}

.bx-body {
  padding: 18px 24px 8px !important;
  color: #1a1500;
}

.bx-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 0;
  font-size: 14px;
  color: #667085;
}

.bx-facts {
  display: grid;
  gap: 8px;
  margin-bottom: 14px;
}
.bx-fact {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 13.5px;
}
.bx-fact-key {
  color: #667085;
  font-weight: 600;
}
.bx-fact-val {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #0c1322;
}
.bx-fact-val.is-ok {
  color: #1f8b3f;
}
.bx-fact-val.is-ok .v-icon {
  color: #1f8b3f;
}
.bx-fact-val.is-miss {
  color: #b25c00;
}
.bx-fact-val.is-miss .v-icon {
  color: #b25c00;
}
.bx-stage {
  font-size: 12.5px;
  background: #f2f4f7;
  border-radius: 6px;
  padding: 3px 9px;
  letter-spacing: .2px;
}

.bx-alert {
  font-size: 12.8px;
  line-height: 1.5;
  background: #f2f4f7;
  border-left: 3px solid #d0d5dd;
  border-radius: 6px;
  padding: 9px 12px;
  margin-bottom: 14px;
  color: #475467;
}
.bx-alert--warn {
  background: #fff8e6;
  border-left-color: #f5c542;
  color: #7a5a00;
}
.bx-alert--error {
  background: #fdecec;
  border-left-color: #e04a4a;
  color: #8f2020;
}

.bx-group {
  margin-bottom: 14px;
}
.bx-group-title {
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #98a2b3;
  margin-bottom: 7px;
}
.bx-group-title--send {
  color: #1f8b3f;
}
.bx-step {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 0;
  font-size: 13.5px;
  flex-wrap: wrap;
}
.bx-step-icon {
  color: #98a2b3;
}
.bx-step-icon.is-ok {
  color: #1f8b3f;
}
.bx-step-label {
  font-weight: 600;
  color: #1a1500;
}
.bx-step--skip .bx-step-label {
  font-weight: 500;
  color: #98a2b3;
}
.bx-step-reason {
  font-size: 12px;
  color: #98a2b3;
}

.bx-actions {
  padding: 8px 18px 16px !important;
  border-top: 1px solid #eceff3;
}

@media (max-width: 600px) {
  .bx-resend {
    width: 100%;
  }
  .bx-btn {
    width: 100%;
  }
  .bx-head {
    padding: 16px 16px 12px;
  }
  .bx-body {
    padding: 14px 16px 6px !important;
  }
  .bx-fact {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
}
</style>
